import React, { useMemo, useState } from "react";

import { Avatar, Badge, Icon, SearchInput } from "../components/ui";

/**
 * The channel rail: one row per conversation the user can see — their
 * store's "<Store> x Retail Circle" channel(s), and (owners) the read-only
 * Retail Circle Updates channel. Master admins see every store's channel,
 * which is why there is a filter: RC staff can have hundreds of rows here,
 * and scrolling a flat list to find one merchant is unusable.
 */

const timeLabel = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  if (sameDay) return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const week = 7 * 24 * 60 * 60 * 1000;
  if (now - date < week) return date.toLocaleDateString([], { weekday: "short" });

  return date.toLocaleDateString([], { day: "numeric", month: "short" });
};

/** Show the filter only when the list is long enough to need one. */
const FILTER_THRESHOLD = 8;

const ChannelList = ({ channels, activeId, onSelect }) => {
  const [query, setQuery] = useState("");
  const showFilter = channels.length >= FILTER_THRESHOLD;

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return channels;

    return channels.filter((channel) =>
      [channel.name, channel.store?.name]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(needle))
    );
  }, [channels, query]);

  return (
    <div className="rc-chat__rail-inner">
      {showFilter && (
        <div className="rc-chat__rail-filter">
          <SearchInput
            value={query}
            onChange={(event) => setQuery(event.target?.value ?? event)}
            placeholder="Find a conversation"
            aria-label="Find a conversation"
          />
        </div>
      )}

      <nav className="rc-chat__channels" aria-label="Conversations">
        {visible.map((channel) => {
          const isUpdates = channel.type === "updates";
          const active = channel.id === activeId;
          const unread = channel.unread_count > 0;

          return (
            <button
              key={channel.id}
              type="button"
              className={[
                "rc-chat__channel",
                active ? "rc-chat__channel--active" : "",
                unread ? "rc-chat__channel--unread" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => onSelect(channel)}
              aria-current={active ? "true" : undefined}
            >
              <Avatar
                name={channel.store?.name || channel.name}
                variant={isUpdates ? "user" : "store"}
                master={isUpdates}
                size="md"
              />

              <span className="rc-chat__channel-main">
                <span className="rc-chat__channel-top">
                  <span className="rc-chat__channel-name">{channel.name}</span>
                  <span className="rc-chat__channel-time">
                    {timeLabel(channel.last_message?.created_at)}
                  </span>
                </span>

                <span className="rc-chat__channel-bottom">
                  {/* Deliberately one text node — "Alex: See you tomorrow"
                      split across spans is no longer findable as a single
                      string, which breaks both screen readers and tests for
                      the sake of styling the name slightly differently. */}
                  <span className="rc-chat__channel-preview">
                    {channel.last_message
                      ? `${channel.last_message.sender_name ? `${channel.last_message.sender_name}: ` : ""}${channel.last_message.preview}`
                      : isUpdates
                        ? "Announcements from Retail Circle"
                        : "Start the conversation"}
                  </span>

                  {unread ? (
                    <Badge tone="accent" size="sm" count>
                      {channel.unread_count > 99 ? "99+" : channel.unread_count}
                    </Badge>
                  ) : (
                    /* An unprovisioned channel works in-app but never
                       reaches Roam, so it is worth marking in the rail
                       rather than only inside the thread. */
                    !channel.is_provisioned && (
                      <span className="rc-chat__channel-flag" title="No Roam group yet">
                        <Icon name="AlertTriangle" size="xs" />
                      </span>
                    )
                  )}
                </span>
              </span>
            </button>
          );
        })}

        {showFilter && visible.length === 0 && (
          <p className="rc-chat__rail-empty">No conversation matches “{query}”.</p>
        )}
      </nav>
    </div>
  );
};

export default ChannelList;
